import React, { useState, useEffect } from 'react';
import { Container, Paper, Typography, Button, Box, TextField, IconButton, Tooltip, Select, MenuItem, FormControl, InputLabel, Fade, Zoom } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import TranslateIcon from '@mui/icons-material/Translate';
import SpeedIcon from '@mui/icons-material/Speed';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import LanguageIcon from '@mui/icons-material/Language';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

function DailyWord() {
  const [selectedLanguage, setSelectedLanguage] = useState('fr');
  const [word, setWord] = useState({
    word: 'Bonjour',
    translation: 'Hello',
    example: 'Bonjour, comment allez-vous?',
    exampleTranslation: 'Hello, how are you?',
    language: 'French'
  });

  const [userTranslation, setUserTranslation] = useState('');
  const [showTranslation, setShowTranslation] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speechRate, setSpeechRate] = useState(1);
  const [showFeedback, setShowFeedback] = useState(false);

  const languageData = {
    fr: {
      code: 'fr-FR',
      name: 'French',
      words: [
        {
          word: 'Bonjour',
          translation: 'Hello',
          example: 'Bonjour, comment allez-vous?',
          exampleTranslation: 'Hello, how are you?'
        },
        {
          word: 'Au revoir',
          translation: 'Goodbye',
          example: 'Au revoir, à bientôt!',
          exampleTranslation: 'Goodbye, see you soon!'
        }
      ]
    },
    de: {
      code: 'de-DE',
      name: 'German',
      words: [
        {
          word: 'Guten Tag',
          translation: 'Good day',
          example: 'Guten Tag, wie geht es Ihnen?',
          exampleTranslation: 'Good day, how are you?'
        },
        {
          word: 'Auf Wiedersehen',
          translation: 'Goodbye',
          example: 'Auf Wiedersehen, bis bald!',
          exampleTranslation: 'Goodbye, see you soon!'
        }
      ]
    },
    zh: {
      code: 'zh-CN',
      name: 'Mandarin Chinese',
      words: [
        {
          word: '你好',
          translation: 'Hello',
          example: '你好，你好吗？',
          exampleTranslation: 'Hello, how are you?'
        },
        {
          word: '再见',
          translation: 'Goodbye',
          example: '再见，下次见！',
          exampleTranslation: 'Goodbye, see you next time!'
        }
      ]
    },
    es: {
      code: 'es-ES',
      name: 'Spanish',
      words: [
        {
          word: 'Hola',
          translation: 'Hello',
          example: 'Hola, ¿cómo estás?',
          exampleTranslation: 'Hello, how are you?'
        },
        {
          word: 'Adiós',
          translation: 'Goodbye',
          example: 'Adiós, hasta pronto!',
          exampleTranslation: 'Goodbye, see you soon!'
        }
      ]
    },
    ru: {
      code: 'ru-RU',
      name: 'Russian',
      words: [
        {
          word: 'Привет',
          translation: 'Hello',
          example: 'Привет, как дела?',
          exampleTranslation: 'Hello, how are you?'
        },
        {
          word: 'До свидания',
          translation: 'Goodbye',
          example: 'До свидания, до скорой встречи!',
          exampleTranslation: 'Goodbye, see you soon!'
        }
      ]
    }
  };

  const handleLanguageChange = (event) => {
    const newLang = event.target.value;
    setSelectedLanguage(newLang);
    const randomWord = languageData[newLang].words[Math.floor(Math.random() * languageData[newLang].words.length)];
    setWord({
      ...randomWord,
      language: languageData[newLang].name
    });
    setShowTranslation(false);
    setUserTranslation('');
    setShowFeedback(false);
  };

  const handlePronunciation = (text, rate = speechRate) => {
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = languageData[selectedLanguage].code;
    utterance.rate = rate;
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    window.speechSynthesis.speak(utterance);
  };

  const handleSpeedChange = () => {
    const newRate = speechRate === 1 ? 0.75 : speechRate === 0.75 ? 1.25 : 1;
    setSpeechRate(newRate);
    if (isSpeaking) {
      handlePronunciation(word.word, newRate);
    }
  };

  const checkTranslation = () => {
    setShowTranslation(true);
    setShowFeedback(true);
    setTimeout(() => setShowFeedback(false), 2000);
  };

  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4,
          background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LanguageIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Word of the Day
            </Typography>
          </Box>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Select Language</InputLabel>
            <Select
              value={selectedLanguage}
              label="Select Language"
              onChange={handleLanguageChange}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.dark',
                },
              }}
            >
              {Object.entries(languageData).map(([code, data]) => (
                <MenuItem key={code} value={code}>{data.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Fade in={true} timeout={1000}>
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <Typography 
              variant="h2" 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {word.word}
            </Typography>
            <Typography 
              variant="subtitle1" 
              color="text.secondary" 
              gutterBottom
              sx={{ 
                fontStyle: 'italic',
                mb: 2
              }}
            >
              [{word.language}]
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
              <Tooltip title={isSpeaking ? "Stop" : "Pronounce"}>
                <IconButton 
                  color="primary" 
                  onClick={() => handlePronunciation(word.word)}
                  sx={{ 
                    fontSize: '2rem',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    }
                  }}
                >
                  {isSpeaking ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title={`Speed: ${speechRate}x`}>
                <IconButton 
                  color="primary" 
                  onClick={handleSpeedChange}
                  sx={{ 
                    fontSize: '2rem',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    }
                  }}
                >
                  <SpeedIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Fade>

        <Fade in={true} timeout={1500}>
          <Box sx={{ my: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Example Sentence:
            </Typography>
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2, 
                mb: 2,
                background: 'rgba(33, 150, 243, 0.05)',
                borderRadius: 2
              }}
            >
              <Typography variant="body1" gutterBottom>
                {word.example}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {word.exampleTranslation}
              </Typography>
            </Paper>
            <Button
              variant="outlined"
              startIcon={<VolumeUpIcon />}
              onClick={() => handlePronunciation(word.example)}
              sx={{ 
                mt: 1,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                },
                transition: 'all 0.2s'
              }}
            >
              Pronounce Example
            </Button>
          </Box>
        </Fade>

        <Fade in={true} timeout={2000}>
          <Box sx={{ my: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Try to translate:
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={userTranslation}
              onChange={(e) => setUserTranslation(e.target.value)}
              placeholder="Enter your translation"
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={checkTranslation}
              startIcon={<TranslateIcon />}
              sx={{ 
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                },
                transition: 'all 0.2s'
              }}
            >
              Check Translation
            </Button>
          </Box>
        </Fade>

        <Zoom in={showTranslation}>
          <Box 
            sx={{ 
              my: 4, 
              p: 3, 
              bgcolor: 'background.default', 
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Correct Translation:
            </Typography>
            <Typography variant="body1">
              {word.translation}
            </Typography>
            {showFeedback && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  p: 1,
                  bgcolor: 'success.main',
                  color: 'white',
                  borderRadius: '0 0 0 8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <EmojiEventsIcon />
                <Typography variant="body2">Great job!</Typography>
              </Box>
            )}
          </Box>
        </Zoom>
      </Paper>
    </Container>
  );
}

export default DailyWord; 